import React, { useEffect, useState } from "react";
import HostelHeader from "./HostelHeader";
import Nav from "../master/NavBar";
import WelcomeBar from "./WelcomeBar";
import { hostelMonthlyReport } from "../../api/HostelService";
import usePrivateLink from "../config/usePrivateLink";
import { Link } from "react-router-dom";
import { LoadingDiv } from "../master/Master";
import HostelReportTabs from "./HostelReportTabs";

export default function HostelMonthlyReport() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [hostel_id] = useState(localStorage.getItem("hostel_id"));

  const [hostelId] = useState(
    JSON.parse(localStorage.getItem("hostel") || "{}"),
  );

  const [fromMonth, setFromMonth] = useState("");
  const [toMonth, setToMonth] = useState("");
  const [months, setMonths] = useState([]);

  const hasAccess = usePrivateLink();

  // ============================================================
  // INR FORMAT
  // ============================================================

  const formatINR = (val) =>
    Number(val || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // ============================================================
  // GENERATE MONTHS
  // ============================================================

  const generateMonths = (from, to) => {
    if (!from || !to) return [];

    let start = new Date(from);
    let end = new Date(to);
    let result = [];

    while (start <= end) {
      result.push(start.toISOString().slice(0, 7));
      start.setMonth(start.getMonth() + 1);
    }

    return result;
  };

  // ============================================================
  // FETCH DATA
  // KEEPING YOUR EXISTING API FUNCTIONALITY
  // ============================================================

  const fetchData = async () => {
    setLoading(true);

    try {
      const params = new URLSearchParams({
        hostel_id,
        from_month: fromMonth,
        to_month: toMonth,
      });

      const res = await hostelMonthlyReport(`?${params.toString()}`);

      setData(res?.data?.data?.students || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // SAME API CALL BEHAVIOUR
  // ============================================================

  useEffect(() => {
    fetchData();
  }, [fromMonth, toMonth]);

  // ============================================================
  // MONTH GENERATION
  // ============================================================

  useEffect(() => {
    setMonths(generateMonths(fromMonth, toMonth));
  }, [fromMonth, toMonth]);

  // ============================================================
  // MONTH VALUE
  // ============================================================

  const getMonthValue = (fees, month) => {
    const record = (fees || []).find((f) => f.month === month);

    if (!record) return "-";

    const paid = Number(record.paid_amount || 0);
    const final = Number(record.final_amount || 0);
    const due = Number(record.left_amount || 0);

    if (record.status == 3) return "OUT";

    if (paid === 0) return `₹${formatINR(due)} Due`;

    if (paid < final)
      return `₹${formatINR(paid)} Paid / ₹${formatINR(due)} Due`;

    return `₹${formatINR(paid)} Paid`;
  };

  // ============================================================
  // COLOR LOGIC
  // ============================================================

  const getColor = (value) => {
    if (value?.includes("Due") && !value?.includes("Paid"))
      return "text-dark fw-bold";

    if (value?.includes("Paid") && value?.includes("Due"))
      return "text-dark fw-bold";

    if (value?.includes("Paid")) return "text-dark fw-bold";

    return "";
  };

  // ============================================================
  // SUMMARY
  // ============================================================

  const getSummary = (fees = []) => {
    let paid = 0;
    let pending = 0;

    fees.forEach((f) => {
      paid += Number(f.paid_amount || 0);
      pending += Number(f.left_amount || 0);
    });

    return {
      paid,
      pending,
      total: paid + pending,
    };
  };

  // ============================================================
  // PRINT FUNCTION
  // ============================================================

  const handlePrint = () => {
    const printContents = document.getElementById("PrintDiv")?.innerHTML;

    if (!printContents) return;

    const printWindow = window.open("", "_blank", "width=1400,height=900");

    if (!printWindow) {
      alert("Please allow popups to print the report.");
      return;
    }

    const reportMonth =
      fromMonth && toMonth
        ? `${new Date(fromMonth).toLocaleString("en-US", {
            month: "long",
            year: "numeric",
          })} - ${new Date(toMonth).toLocaleString("en-US", {
            month: "long",
            year: "numeric",
          })}`
        : "Monthly Report";

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>

        <title>${hostelId?.name || "Hostel"} - Monthly Report</title>

        <meta charset="UTF-8" />

        <style>

          @page {
            size: A4 landscape;
            margin: 8mm;
          }

          * {
            box-sizing: border-box;
          }

          html,
          body {
            margin: 0;
            padding: 0;
            background: #fff !important;
            color: #000 !important;
            font-family: Arial, Helvetica, sans-serif;
          }

          body {
            font-size: 9px;
          }

          .print-wrapper {
            width: 100%;
          }

          .print-header {
            text-align: center;
            margin-bottom: 12px;
          }

          .print-header h1 {
            margin: 0;
            font-size: 20px;
            font-weight: 700;
          }

          .print-header h2 {
            margin: 4px 0 0;
            font-size: 13px;
            font-weight: 500;
          }

          .print-period {
            margin-top: 5px;
            font-size: 10px;
            font-weight: 600;
          }

          .print-info {
            display: flex;
            justify-content: space-between;
            border-top: 1px solid #999;
            border-bottom: 1px solid #999;
            padding: 5px 0;
            margin-bottom: 10px;
            font-size: 9px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            table-layout: auto;
          }

          th,
          td {
            border: 1px solid #777 !important;
            padding: 5px 4px !important;
            text-align: center;
            vertical-align: middle;
            color: #000 !important;
            background: #fff !important;
            white-space: nowrap;
          }

          th {
            background: #eeeeee !important;
            font-weight: 700;
            font-size: 8px;
          }

          td {
            font-size: 8px;
          }

          td:nth-child(2) {
            text-align: left;
            font-weight: 600;
          }

          .print-footer {
            display: flex;
            justify-content: space-between;
            margin-top: 10px;
            padding-top: 5px;
            border-top: 1px solid #999;
            font-size: 8px;
          }

          thead {
            display: table-header-group;
          }

          tr {
            page-break-inside: avoid;
          }

          .text-center {
            text-align: center !important;
          }

          .fw-bold {
            font-weight: 700 !important;
          }

          .text-dark {
            color: #000 !important;
          }

          .table {
            margin-bottom: 0 !important;
          }

          .table-bordered {
            border: 1px solid #777 !important;
          }

          .report-title {
            margin-bottom: 3px;
          }

          .report-subtitle {
            margin-bottom: 2px;
          }

          @media print {

            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

          }

        </style>

      </head>

      <body>

        <div class="print-wrapper">

          <div class="print-header">

            <h1 class="report-title">
              ${hostelId?.name || "Hostel"}
            </h1>

            <h2 class="report-subtitle">
              Hostel Student Monthly Report
            </h2>

            <div class="print-period">
              ${reportMonth}
            </div>

          </div>

          <div class="print-info">

            <div>
              Total Students:
              <strong>${data?.length || 0}</strong>
            </div>

            <div>
              Generated On:
              <strong>
                ${new Date().toLocaleDateString("en-IN")}
              </strong>
            </div>

          </div>

          ${printContents}

          <div class="print-footer">

            <div>
              ${hostelId?.name || "Hostel"}
            </div>

            <div>
              Hostel Student Monthly Report
            </div>

          </div>

        </div>

      </body>

      </html>
    `);

    printWindow.document.close();

    printWindow.focus();

    setTimeout(() => {
      printWindow.print();

      setTimeout(() => {
        printWindow.close();
      }, 500);
    }, 300);
  };

  return (
    <>
      {/* =========================================================
          LOADING OVERLAY
      ========================================================= */}

      {loading && <LoadingDiv />}

      {/* =========================================================
          LAYOUT
      ========================================================= */}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <HostelHeader />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-xxl flex-grow-1 container-p-y">
                {/* <WelcomeBar /> */}

                {/* =================================================
                    PAGE HEADER
                ================================================= */}

                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded-3 bg-label-primary d-flex align-items-center justify-content-center"
                      style={{
                        width: 46,
                        height: 46,
                      }}
                    >
                      <i className="ri-file-chart-line fs-4 text-primary" />
                    </div>

                    <div>
                      <h4 className="mb-1 fw-bold">Hostel Student Report</h4>

                      <p className="mb-0 text-muted">
                        Monthly student fee collection report
                      </p>
                    </div>
                  </div>

                  {/* {data?.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-primary d-flex align-items-center gap-2 px-4"
                      onClick={handlePrint}
                    >
                      <i className="ri-printer-line fs-5" />
                      Print Report
                    </button>
                  )} */}
                </div>

                {/* =================================================
                    REPORT NAVIGATION
                ================================================= */}

                <HostelReportTabs />

                {/* =================================================
                    FILTER CARD
                ================================================= */}

                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-header border-bottom">
                    <div className="d-flex align-items-center gap-2">
                      <div
                        className="rounded-2 bg-label-primary d-flex align-items-center justify-content-center"
                        style={{
                          width: 38,
                          height: 38,
                        }}
                      >
                        <i className="ri-filter-3-line text-primary" />
                      </div>

                      <div>
                        <h5 className="mb-0">Report Filters</h5>

                        <small className="text-muted">
                          Select the month range for the report
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="row g-3">
                      {/* FROM MONTH */}

                      <div className="col-md-4 col-lg-3">
                        <div className="form-floating form-floating-outline">
                          <input
                            type="month"
                            className="form-control"
                            value={fromMonth}
                            onChange={(e) => setFromMonth(e.target.value)}
                          />

                          <label>From Month</label>
                        </div>
                      </div>

                      {/* TO MONTH */}

                      <div className="col-md-4 col-lg-3">
                        <div className="form-floating form-floating-outline">
                          <input
                            type="month"
                            className="form-control"
                            value={toMonth}
                            onChange={(e) => setToMonth(e.target.value)}
                          />

                          <label>To Month</label>
                        </div>
                      </div>

                      {/* RANGE INFO */}

                      {months?.length > 0 && (
                        <div className="col-md-4 col-lg">
                          <div
                            className="h-100 rounded-3 bg-label-primary px-3 d-flex align-items-center"
                            style={{
                              minHeight: 58,
                            }}
                          >
                            <i className="ri-calendar-check-line fs-4 text-primary me-2" />

                            <div>
                              <small className="text-muted d-block">
                                Reporting Period
                              </small>

                              <span className="fw-semibold">
                                {months.length}{" "}
                                {months.length === 1 ? "Month" : "Months"}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* =================================================
                    SUMMARY CARDS
                ================================================= */}

                {data?.length > 0 && (
                  <div className="row g-3 mb-4">
                    {/* STUDENTS */}

                    <div className="col-sm-6 col-xl-3">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <small className="text-muted">
                                Total Students
                              </small>

                              <h4 className="mb-0 mt-1 fw-bold">
                                {data.length}
                              </h4>
                            </div>

                            <div className="bg-label-primary rounded-3 p-2">
                              <i className="ri-group-line fs-4 text-primary" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* MONTHS */}

                    <div className="col-sm-6 col-xl-3">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <small className="text-muted">
                                Report Months
                              </small>

                              <h4 className="mb-0 mt-1 fw-bold">
                                {months.length}
                              </h4>
                            </div>

                            <div className="bg-label-info rounded-3 p-2">
                              <i className="ri-calendar-line fs-4 text-info" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* HOSTEL */}

                    <div className="col-sm-6 col-xl-3">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <small className="text-muted">Hostel</small>

                              <h6 className="mb-0 mt-2 fw-bold text-truncate">
                                {hostelId?.name || "-"}
                              </h6>
                            </div>

                            <div className="bg-label-success rounded-3 p-2">
                              <i className="ri-building-line fs-4 text-success" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* PRINT */}

                    <div className="col-sm-6 col-xl-3">
                      <div
                        className="card border-0 shadow-sm h-100"
                        style={{ cursor: "pointer" }}
                        onClick={handlePrint}
                      >
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <small className="text-muted">
                                Report Action
                              </small>

                              <h6 className="mb-0 mt-2 fw-bold">
                                Print Report
                              </h6>
                            </div>

                            <div className="bg-label-primary rounded-3 p-2">
                              <i className="ri-printer-line fs-4 text-primary" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* =================================================
                    REPORT TABLE CARD
                ================================================= */}

                <div className="card border-0 shadow-sm">
                  <div className="card-header border-bottom py-3">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                      <div>
                        <h5 className="mb-1 fw-bold">Hostel Monthly Report</h5>

                        <small className="text-muted">
                          {data?.length > 0
                            ? `${data.length} students found`
                            : "No report data available"}
                        </small>
                      </div>

                      {data?.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-label-primary"
                          onClick={handlePrint}
                        >
                          <i className="ri-printer-line me-1" />
                          Print
                        </button>
                      )}
                    </div>
                  </div>

                  {/* =================================================
                      REPORT
                  ================================================= */}

                  <div className="card-body p-0">
                    {data?.length > 0 ? (
                      <div
                        className="table-responsive"
                        id="PrintDiv"
                        style={{
                          maxHeight: "65vh",
                          overflow: "auto",
                        }}
                      >
                        {/* PRINT HEADER - HIDDEN ON SCREEN */}

                        <div className="d-none">
                          <div className="text-center mb-3">
                            <h4 className="mb-1">{hostelId?.name}</h4>

                            <h5 className="mb-1">
                              Hostel Student Monthly Report
                            </h5>

                            <small>
                              {fromMonth} to {toMonth}
                            </small>
                          </div>
                        </div>

                        <table
                          className="table table-bordered table-hover text-center align-middle mb-0"
                          style={{
                            minWidth: `${900 + months.length * 150}px`,
                          }}
                        >
                          <thead
                            className="table-light"
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 5,
                            }}
                          >
                            <tr>
                              <th
                                style={{
                                  minWidth: 55,
                                }}
                              >
                                #
                              </th>

                              <th
                                className="text-start"
                                style={{
                                  minWidth: 210,
                                }}
                              >
                                <i className="ri-user-line me-1" />
                                Name
                              </th>

                              <th
                                style={{
                                  minWidth: 130,
                                }}
                              >
                                <i className="ri-phone-line me-1" />
                                Mobile
                              </th>

                              <th
                                style={{
                                  minWidth: 120,
                                }}
                              >
                                Joining
                              </th>

                              <th
                                style={{
                                  minWidth: 120,
                                }}
                              >
                                Leaving
                              </th>

                              {fromMonth &&
                                toMonth &&
                                months.map((m) => (
                                  <th
                                    key={m}
                                    style={{
                                      minWidth: 145,
                                    }}
                                  >
                                    <div className="fw-bold">
                                      {new Date(m).toLocaleString("en-US", {
                                        month: "short",
                                        year: "numeric",
                                      })}
                                    </div>
                                  </th>
                                ))}

                              <th
                                style={{
                                  minWidth: 130,
                                }}
                              >
                                Total
                              </th>

                              <th
                                style={{
                                  minWidth: 145,
                                }}
                              >
                                Total Pending
                              </th>

                              <th
                                style={{
                                  minWidth: 135,
                                }}
                              >
                                Total Paid
                              </th>
                            </tr>
                          </thead>

                          <tbody className="table-lite">
                            {data.map((s, i) => {
                              const fees = s.hostel_fees || [];

                              const { paid, pending, total } = getSummary(fees);

                              return (
                                <tr key={i}>
                                  {/* NUMBER */}

                                  <td className="fw-semibold">
                                    <span
                                      className="d-inline-flex align-items-center justify-content-center rounded-circle bg-label-secondary"
                                      style={{
                                        width: 30,
                                        height: 30,
                                      }}
                                    >
                                      {i + 1}
                                    </span>
                                  </td>

                                  {/* NAME */}

                                  <td className="text-start">
                                    <div className="d-flex align-items-center gap-2">
                                      <div
                                        className="d-flex align-items-center justify-content-center rounded-circle bg-label-primary flex-shrink-0"
                                        style={{
                                          width: 36,
                                          height: 36,
                                        }}
                                      >
                                        <i className="ri-user-line text-primary" />
                                      </div>

                                      <div>
                                        <div className="fw-bold">
                                          {s.student_name}
                                        </div>

                                        {s.registration_no && (
                                          <small className="text-muted">
                                            Reg: {s.registration_no}
                                          </small>
                                        )}
                                      </div>
                                    </div>
                                  </td>

                                  {/* MOBILE */}

                                  <td className="fw-semibold">{s.mobile}</td>

                                  {/* JOINING */}

                                  <td className="fw-semibold">
                                    {new Date(
                                      s.joining_date,
                                    ).toLocaleDateString()}
                                  </td>

                                  {/* LEAVING */}

                                  <td className="fw-semibold">
                                    {s.last_date
                                      ? new Date(
                                          s.last_date,
                                        ).toLocaleDateString()
                                      : "-"}
                                  </td>

                                  {/* MONTHLY DATA */}

                                  {fromMonth &&
                                    toMonth &&
                                    months.map((m) => {
                                      const value = getMonthValue(fees, m);

                                      let cellClass = "";

                                      if (value === "OUT") {
                                        cellClass = "bg-label-danger";
                                      } else if (
                                        value?.includes("Due") &&
                                        value?.includes("Paid")
                                      ) {
                                        cellClass = "bg-label-warning";
                                      } else if (value?.includes("Due")) {
                                        cellClass = "bg-label-warning";
                                      } else if (value?.includes("Paid")) {
                                        cellClass = "bg-label-success";
                                      }

                                      return (
                                        <td key={m} className={cellClass}>
                                          <span className={getColor(value)}>
                                            {value}
                                          </span>
                                        </td>
                                      );
                                    })}

                                  {/* TOTAL */}

                                  <td className="fw-bold">
                                    ₹{formatINR(total)}
                                  </td>

                                  {/* PENDING */}

                                  <td className="fw-bold">
                                    <span className="badge bg-label-warning px-3 py-2">
                                      ₹{formatINR(pending)}
                                    </span>
                                  </td>

                                  {/* PAID */}

                                  <td className="fw-bold">
                                    <span className="badge bg-label-success px-3 py-2">
                                      ₹{formatINR(paid)}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-5 px-3">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle bg-label-secondary mx-auto mb-3"
                          style={{
                            width: 70,
                            height: 70,
                          }}
                        >
                          <i className="ri-file-search-line fs-2 text-muted" />
                        </div>

                        <h5 className="fw-bold mb-2">No Report Data</h5>

                        <p className="text-muted mb-0">
                          No hostel student records are available for the
                          selected period.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* =================================================
                      FOOTER / LEGEND
                  ================================================= */}

                  {data?.length > 0 && (
                    <div className="card-footer border-top">
                      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                        <small className="text-muted">
                          Showing <strong>{data.length}</strong> students
                        </small>

                        <div className="d-flex flex-wrap gap-3">
                          <small className="text-muted">
                            <span className="badge bg-label-success me-1">
                              Paid
                            </span>
                            Paid
                          </small>

                          <small className="text-muted">
                            <span className="badge bg-label-warning me-1">
                              Due
                            </span>
                            Pending
                          </small>

                          <small className="text-muted">
                            <span className="badge bg-label-danger me-1">
                              OUT
                            </span>
                            Student left
                          </small>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
